import React, { useState, useEffect } from 'react';
import cacheManager from '../CacheManager';
import { formatTime } from '../js/dateFormatter';
import { showError } from '../utils/toast';

const PgNovoAgendamento = () => {
  const [espacos, setEspacos] = useState([]);
  const [selectedEspaco, setSelectedEspaco] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [agendamentosOcupados, setAgendamentosOcupados] = useState([]);
  const [itensById, setItensById] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);

  // Gerar todos os intervalos de 8h às 20h
  const timeSlots = [];
  for (let hour = 8; hour < 20; hour++) {
    timeSlots.push({
      start: `${hour.toString().padStart(2, '0')}:00`,
      end: `${(hour + 1).toString().padStart(2, '0')}:00`,
      startHour: hour,
      endHour: hour + 1
    });
  }

  useEffect(() => {
    loadEspacos();
  }, []);

  useEffect(() => {
    if (selectedEspaco && selectedDate) {
      loadAgendamentosOcupados();
    }
  }, [selectedEspaco, selectedDate]);

  const loadEspacos = async () => {
    try {
      const espacosData = await cacheManager.getEspacosCache();
      // Filtrar estações individuais de trabalho (1 a 20) e Lançamentos
      const espacosFiltrados = espacosData.filter(espaco => {
        const titulo = espaco.titulo || '';
        
        // Remove "Lançamentos"
        if (titulo === 'Lançamentos') {
          return false;
        }
        
        // Verifica se é uma estação individual de 1 a 20
        for (let i = 1; i <= 20; i++) {
          if (titulo === `Estação individual de trabalho ${i}`) {
            return false; // Remove essas estações
          }
        }
        return true; // Mantém os demais espaços
      });
      
      // Ordenar por título
      espacosFiltrados.sort((a, b) => {
        const tituloA = (a.titulo || '').toLowerCase();
        const tituloB = (b.titulo || '').toLowerCase();
        return tituloA.localeCompare(tituloB);
      });
      
      setEspacos(espacosFiltrados);
    } catch (error) {
      console.error('Erro ao carregar espaços:', error);
    }
  };

  const loadAgendamentosOcupados = async () => {
    try {
      setIsLoading(true);
      // Busca TODOS os agendamentos usando método do cache (sem filtro de cliente)
      const agendamentos = await cacheManager.getAllAgendamentosCache();
      const itens = await cacheManager.getItensById();
      setItensById(itens);

      // Filtrar agendamentos do espaço e data selecionados
      const ocupados = agendamentos.filter(ag => {
        const dataAgendamento = new Date(ag.data_inicio_agendamento);
        const dataAgendamentoStr = dataAgendamento.toISOString().split('T')[0];
        
        const item = itens[ag.id_item];
        const espacoDoItem = item?.id_espaco;
        
        // Verifica se é o espaço selecionado e se é o mesmo dia
        return String(espacoDoItem) === String(selectedEspaco) && 
               dataAgendamentoStr === selectedDate;
      });

      // Ordenar por horário de início
      ocupados.sort((a, b) => 
        new Date(a.data_inicio_agendamento) - new Date(b.data_inicio_agendamento)
      );

      setAgendamentosOcupados(ocupados);
    } catch (error) {
      console.error('Erro ao carregar agendamentos ocupados:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getEspacoNome = (id) => {
    const espaco = espacos.find(e => String(e.id_espaco) === String(id));
    return espaco?.titulo || 'Espaço';
  };

  // Verifica se um intervalo está ocupado
  const isSlotOccupied = (slot) => {
    if (!selectedDate) return false;

    return agendamentosOcupados.some(ag => {
      const dataInicio = new Date(ag.data_inicio_agendamento);
      const dataFim = new Date(ag.data_fim_agendamento);
      
      const slotStart = new Date(`${selectedDate}T${slot.start}:00`);
      const slotEnd = new Date(`${selectedDate}T${slot.end}:00`);
      
      // Verifica se há sobreposição entre o agendamento e o slot
      return (dataInicio < slotEnd && dataFim > slotStart);
    });
  };

  const toggleSlot = (slot) => {
    if (isSlotOccupied(slot)) return; // Não permite selecionar slots ocupados
    
    const slotKey = `${slot.start}-${slot.end}`;
    setSelectedSlots(prev => {
      if (prev.includes(slotKey)) {
        return prev.filter(s => s !== slotKey);
      } else {
        return [...prev, slotKey];
      }
    });
  };

  // Formatar data para exibição
  const formatDateForMessage = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Agrupar intervalos consecutivos
  const groupConsecutiveSlots = () => {
    if (selectedSlots.length === 0) return [];
    
    // Ordenar os slots selecionados
    const sorted = [...selectedSlots].sort();
    const groups = [];
    let currentGroup = null;
    
    sorted.forEach(slotKey => {
      const [start, end] = slotKey.split('-');
      const startHour = parseInt(start.split(':')[0]);
      const endHour = parseInt(end.split(':')[0]);
      
      if (!currentGroup) {
        currentGroup = { start, end };
      } else {
        const currentEndHour = parseInt(currentGroup.end.split(':')[0]);
        if (startHour === currentEndHour) {
          // Intervalo consecutivo, estende o grupo
          currentGroup.end = end;
        } else {
          // Novo grupo
          groups.push(currentGroup);
          currentGroup = { start, end };
        }
      }
    });
    
    if (currentGroup) {
      groups.push(currentGroup);
    }
    
    return groups;
  };

  const sendWhatsApp = () => {
    if (!selectedEspaco || !selectedDate || selectedSlots.length === 0) {
      showError('Por favor, selecione o espaço, data e pelo menos um horário');
      return;
    }

    const espacoNome = getEspacoNome(selectedEspaco);
    const dataFormatada = formatDateForMessage(selectedDate);
    const grupos = groupConsecutiveSlots();
    const intervalosTexto = grupos.map(g => `${g.start} às ${g.end}`).join(', ');
    const intervaloOuIntervalos = grupos.length === 1 ? 'No intervalo:' : 'Nos intervalos:';

    const mensagem = `Olá!\nDesejo agendar a ${espacoNome} no dia ${dataFormatada}\n${intervaloOuIntervalos} ${intervalosTexto}`;
    const mensagemEncoded = encodeURIComponent(mensagem);
    const whatsappUrl = `https://wa.me/5521979621222?text=${mensagemEncoded}`;
    
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="container mt-4">
      <div className="row mb-4">
        <div className="col-12">
          <h2>Novo Agendamento</h2>
          <p className="text-muted">Selecione o espaço e a data para ver a disponibilidade</p>
        </div>
      </div>

      {/* Formulário de Seleção */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="card-title mb-4">Selecione o Espaço e Data</h5>
              
              <div className="mb-3">
                <label className="form-label">Espaço</label>
                <select
                  className="form-select form-select-lg"
                  value={selectedEspaco}
                  onChange={(e) => setSelectedEspaco(e.target.value)}
                >
                  <option value="">Selecione um espaço</option>
                  {espacos.map((espaco) => (
                    <option key={espaco.id_espaco} value={espaco.id_espaco}>
                      {espaco.titulo}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label">Data</label>
                <input
                  type="date"
                  className="form-control form-control-lg"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Horários Disponíveis/Ocupados */}
        <div className="col-md-6">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="card-title mb-4">
                Horários - {selectedDate && selectedEspaco ? getEspacoNome(selectedEspaco) : 'Selecione espaço e data'}
              </h5>
              
              {!selectedEspaco || !selectedDate ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-calendar-check fs-1 mb-3 d-block"></i>
                  <p>Selecione um espaço e data para ver a disponibilidade</p>
                </div>
              ) : isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              ) : (
                <div className="list-group">
                  {timeSlots.map((slot, index) => {
                    const occupied = isSlotOccupied(slot);
                    const slotKey = `${slot.start}-${slot.end}`;
                    const isSelected = selectedSlots.includes(slotKey);
                    
                    return (
                      <div
                        key={index}
                        className={`list-group-item d-flex justify-content-between align-items-center ${
                          occupied ? 'list-group-item-danger' : 
                          isSelected ? 'list-group-item-success' : ''
                        }`}
                        style={{ 
                          cursor: occupied ? 'not-allowed' : 'pointer',
                          opacity: occupied ? 0.7 : 1
                        }}
                        onClick={() => !occupied && toggleSlot(slot)}
                      >
                        <div>
                          <strong>{slot.start} - {slot.end}</strong>
                        </div>
                        <div>
                          {occupied ? (
                            <span className="badge bg-danger">
                              <i className="bi bi-x-circle me-1"></i>
                              Ocupado
                            </span>
                          ) : isSelected ? (
                            <span className="badge bg-success">
                              <i className="bi bi-check-circle-fill me-1"></i>
                              Selecionado
                            </span>
                          ) : (
                            <span className="badge bg-success">
                              <i className="bi bi-check-circle me-1"></i>
                              Disponível
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Botão WhatsApp */}
      {selectedSlots.length > 0 && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm bg-success text-white">
              <div className="card-body text-center">
                <h5 className="card-title">
                  {selectedSlots.length} horário(s) selecionado(s)
                </h5>
                <button 
                  className="btn btn-light btn-lg mt-2"
                  onClick={sendWhatsApp}
                >
                  <i className="bi bi-whatsapp me-2"></i>
                  Agendar via WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Informações Adicionais */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="alert alert-info">
            <h6 className="alert-heading">
              <i className="bi bi-info-circle"></i> Como fazer seu agendamento
            </h6>
            <p className="mb-0">
              Selecione os horários disponíveis (em verde) que deseja reservar e entre em contato conosco 
              através do WhatsApp ou telefone informando os horários escolhidos. 
              Nossa equipe confirmará sua reserva.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PgNovoAgendamento;
